import { useEffect, useState, type RefObject } from "react";

/** How many cells wide an open one-row PixelLoop is (three cells, two cell-wide gaps). */
const OPEN_CELLS = 5;

/**
 * Whether a section title row (a one-row PixelLoop, then the title) still
 * fits on one line with the loop open. Opening widens the loop by four cells;
 * where that would wrap the title, the page below would jump, so the loop
 * stays collapsed instead. Measured from the title's own text and font, so it
 * holds for every language and text size, and re-measured on resize and once
 * the web font has loaded. True until it can measure (and where it cannot).
 *
 * @param rowRef the flex row holding the PixelLoop and the title.
 */
export function useTitleMarkFits(rowRef: RefObject<HTMLElement | null>): boolean {
  const [fits, setFits] = useState(true);

  useEffect(() => {
    const row = rowRef.current;
    const container = row?.parentElement;
    if (!row || !container) return undefined;
    const context = document.createElement("canvas").getContext("2d");
    if (!context) return undefined;

    const measure = () => {
      const title = row.querySelector("h1, h2, h3");
      const loop = row.querySelector<HTMLElement>("[data-rows]");
      if (!title || !loop) return;
      const style = getComputedStyle(title);
      context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const text = context.measureText(title.textContent ?? "").width;
      // A one-row loop is one cell tall, open or collapsed.
      const unit = loop.getBoundingClientRect().height;
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      setFits(unit * OPEN_CELLS + gap + text <= container.clientWidth);
    };

    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(container);
    let active = true;
    void document.fonts?.ready.then(() => active && measure());
    return () => {
      active = false;
      observer?.disconnect();
    };
  }, [rowRef]);

  return fits;
}
